import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import {
  consumeDesignStream,
  describeDesignProgress,
} from 'features/sentinelDesign/designStream'
import { describeRequestError } from 'features/sentinelDesign/requestError'
import {
  SENTINEL_STAGES,
  type SentinelStageId,
} from 'features/sentinelDesign/stages'
import type {
  SentinelDesignContext,
  SentinelDesignContextRequest,
  SentinelDesignResponse,
  SentinelDesignResult,
  SentinelEvaluation,
  SentinelEvaluationResult,
  SentinelPlan,
  SentinelRunReceipt,
} from 'features/sentinelDesign/types'
import { mergeLoadedConfig } from 'features/timeSeries/experiment/types'
import { configToPayload } from 'features/timeSeries/experiment/useExperimentApi'
import { useCallback, useRef, useState } from 'react'

/** Keep the progress log bounded; the tail is the informative part. */
const MAX_PROGRESS_LINES = 60

/**
 * One log per stage, rather than one log the whole flow shares.
 *
 * A single shared array had to be cleared at the start of every call, so
 * pressing Run destroyed the record of how the context was built — without
 * anybody navigating anywhere. Each stage now clears only its own.
 */
export type SentinelStageLogs = Record<SentinelStageId, string[]>

const emptyStageLogs = (): SentinelStageLogs =>
  Object.fromEntries(
    SENTINEL_STAGES.map((stage) => [stage, [] as string[]])
  ) as SentinelStageLogs

export interface SentinelDesignApi {
  context: SentinelDesignContext | null
  contextLoading: boolean
  contextError: string | null
  /**
   * The context this hook is working on, known from the build stream's FIRST
   * frame rather than its last. The pull and the plots take minutes, and the id
   * is the only thing a reloaded page can come back to, so it is available
   * long before the bundle is.
   */
  contextId: string | null
  buildContext: (
    request: SentinelDesignContextRequest
  ) => Promise<SentinelDesignContext | null>
  resetContext: () => void
  /**
   * Take over a bundle that was read back off its entry rather than built here,
   * so a reloaded page can design and run against it without paying for the
   * historian pull a second time.
   */
  adoptContext: (context: SentinelDesignContext) => void

  design: SentinelDesignResult | null
  designLoading: boolean
  designError: string | null
  requestDesign: (notes: string) => Promise<SentinelDesignResult | null>

  runLoading: boolean
  runError: string | null
  runEntryId: string | null
  /**
   * Fit a plan and answer the run entry's id — the fit itself is never in hand
   * here, only on the entry, so the id is the whole result.
   */
  startRun: (plan: SentinelPlan, name: string) => Promise<string | null>

  evaluation: SentinelEvaluation | null
  evaluateLoading: boolean
  evaluateError: string | null
  evaluate: (entryId?: string) => Promise<SentinelEvaluation | null>

  /** Progress lines this session saw, kept per stage. */
  progress: SentinelStageLogs
  /**
   * Fetch a context plot as an object URL (the route needs the auth header).
   * Takes the context id so a bundle read back off an entry can show its plots
   * without this hook having built it.
   */
  fetchTagPlot: (tag: string, contextId?: string) => Promise<string | null>
}

/**
 * The four sentinel-design calls. Streaming follows the Experiment Lab's
 * pattern (raw `fetch` with the bearer header, line-buffered `data:` frames);
 * plain reads go through `authorizedAxios` like the rest of the app.
 */
export const useSentinelDesignApi = (
  siteId: string | null
): SentinelDesignApi => {
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios, authorization } = useGeckoAuth()
  const base = siteId
    ? `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteId)}/sentinel-design`
    : null

  const [context, setContext] = useState<SentinelDesignContext | null>(null)
  const [contextId, setContextId] = useState<string | null>(null)
  const [contextLoading, setContextLoading] = useState(false)
  const [contextError, setContextError] = useState<string | null>(null)

  const [design, setDesign] = useState<SentinelDesignResult | null>(null)
  const [designLoading, setDesignLoading] = useState(false)
  const [designError, setDesignError] = useState<string | null>(null)

  const [runLoading, setRunLoading] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)
  const [runEntryId, setRunEntryId] = useState<string | null>(null)

  const [evaluation, setEvaluation] = useState<SentinelEvaluation | null>(null)
  const [evaluateLoading, setEvaluateLoading] = useState(false)
  const [evaluateError, setEvaluateError] = useState<string | null>(null)

  const [progress, setProgress] = useState<SentinelStageLogs>(emptyStageLogs)

  // One in-flight stream at a time per hook instance; a new call supersedes.
  const abortRef = useRef<AbortController | null>(null)
  const contextIdRef = useRef<string | null>(null)

  const clearStageLog = useCallback((stage: SentinelStageId) => {
    setProgress((prev) => ({ ...prev, [stage]: [] }))
  }, [])

  const pushProgress = useCallback(
    (stage: SentinelStageId, frame: Record<string, unknown>) => {
      const line = describeDesignProgress(frame)
      setProgress((prev) => ({
        ...prev,
        [stage]: [...prev[stage].slice(-MAX_PROGRESS_LINES), line],
      }))
    },
    []
  )

  /** POST a streaming route and return its final result frame. */
  const postStream = useCallback(
    async <TResult>(
      stage: SentinelStageId,
      path: string,
      body: unknown,
      onProgress?: (frame: Record<string, unknown>) => void
    ): Promise<TResult | null> => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const response = await fetch(`${base}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(authorization ? { Authorization: authorization } : {}),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `${path} HTTP ${response.status}`)
      }
      return consumeDesignStream<TResult>(response.body, {
        onProgress: (frame) => {
          pushProgress(stage, frame)
          onProgress?.(frame)
        },
      })
    },
    [authorization, base, pushProgress]
  )

  const rememberContextId = useCallback((id: string) => {
    contextIdRef.current = id
    setContextId(id)
  }, [])

  const resetContext = useCallback(() => {
    abortRef.current?.abort()
    contextIdRef.current = null
    setContextId(null)
    setContext(null)
    setContextError(null)
    setDesign(null)
    setDesignError(null)
    setRunError(null)
    setRunEntryId(null)
    setEvaluation(null)
    setEvaluateError(null)
    setProgress(emptyStageLogs())
  }, [])

  const adoptContext = useCallback(
    (adopted: SentinelDesignContext) => {
      rememberContextId(adopted.context_id)
      setContext(adopted)
      setContextError(null)
    },
    [rememberContextId]
  )

  const buildContext = useCallback(
    async (request: SentinelDesignContextRequest) => {
      if (!base) return null
      setContextLoading(true)
      setContextError(null)
      setDesign(null)
      setRunEntryId(null)
      setEvaluation(null)
      setProgress(emptyStageLogs())
      try {
        const result = await postStream<SentinelDesignContext>(
          'context',
          '/context/stream',
          request,
          // The first frame names the context, minutes before the bundle
          // exists. Taking it here is what lets a reload mid-build come back
          // to the build instead of starting a second one.
          (frame) => {
            if (typeof frame.context_id === 'string')
              rememberContextId(frame.context_id)
          }
        )
        if (!result) throw new Error('The context stream returned no bundle')
        rememberContextId(result.context_id)
        setContext(result)
        return result
      } catch (err) {
        if ((err as Error).name === 'AbortError') return null
        setContextError(describeRequestError(err))
        return null
      } finally {
        setContextLoading(false)
      }
    },
    [base, postStream, rememberContextId]
  )

  const requestDesign = useCallback(
    async (notes: string) => {
      const contextId = contextIdRef.current
      if (!base || !contextId) return null
      setDesignLoading(true)
      setDesignError(null)
      clearStageLog('plan')
      try {
        const raw = await postStream<SentinelDesignResponse>(
          'plan',
          '/design/stream',
          { context_id: contextId, notes }
        )
        if (!raw?.plan) throw new Error('The design stream returned no plan')
        // A knob the agent didn't name must still be editable, so the plan is
        // merged onto the lab defaults rather than used as-is.
        const result: SentinelDesignResult = {
          ...raw,
          plan: mergeLoadedConfig(raw.plan),
        }
        setDesign(result)
        return result
      } catch (err) {
        if ((err as Error).name === 'AbortError') return null
        setDesignError(describeRequestError(err))
        return null
      } finally {
        setDesignLoading(false)
      }
    },
    [base, clearStageLog, postStream]
  )

  const startRun = useCallback(
    async (plan: SentinelPlan, name: string) => {
      const contextId = contextIdRef.current
      if (!base || !contextId) return null
      setRunLoading(true)
      setRunError(null)
      setEvaluation(null)
      clearStageLog('run')
      clearStageLog('evaluate')
      // The id has to survive the stream in a local as well as in state:
      // reading it back off `runEntryId` here would see the value this closure
      // captured, not the one the frames just delivered.
      let streamedEntryId: string | null = null
      try {
        const receipt = await postStream<SentinelRunReceipt>(
          'run',
          '/run/stream',
          {
            context_id: contextId,
            plan: configToPayload(plan),
            name,
          },
          // Every progress frame carries the run's entry id, starting with the
          // first — evaluate is keyed on it, so it is available long before the
          // fit finishes.
          (frame) => {
            if (typeof frame.entry_id === 'string') {
              streamedEntryId = frame.entry_id
              setRunEntryId(frame.entry_id)
            }
          }
        )
        const entryId = receipt?.entry_id ?? streamedEntryId
        if (!entryId) throw new Error('The run finished without a sentinel')
        setRunEntryId(entryId)
        return entryId
      } catch (err) {
        if ((err as Error).name === 'AbortError') return null
        setRunError(describeRequestError(err))
        return null
      } finally {
        setRunLoading(false)
      }
    },
    [base, clearStageLog, postStream]
  )

  const evaluate = useCallback(
    async (entryId?: string) => {
      // The caller's id wins so a run opened from the sidebar — one this hook
      // never started, and therefore has no `runEntryId` for — can be evaluated.
      const target = entryId || runEntryId
      if (!base || !target) return null
      setEvaluateLoading(true)
      setEvaluateError(null)
      clearStageLog('evaluate')
      try {
        const result = await postStream<SentinelEvaluationResult>(
          'evaluate',
          '/evaluate/stream',
          { entry_id: target }
        )
        if (!result?.evaluation) {
          throw new Error('The evaluate stream returned no verdict')
        }
        setEvaluation(result.evaluation)
        return result.evaluation
      } catch (err) {
        if ((err as Error).name === 'AbortError') return null
        setEvaluateError(describeRequestError(err))
        return null
      } finally {
        setEvaluateLoading(false)
      }
    },
    [base, clearStageLog, postStream, runEntryId]
  )

  /**
   * Context plots come from an authorized fim-service route, so they cannot be
   * an `<img src>` directly — fetch the bytes and hand back an object URL the
   * caller revokes.
   */
  const fetchTagPlot = useCallback(
    async (tag: string, forContextId?: string) => {
      const contextId = forContextId || contextIdRef.current
      if (!base || !contextId) return null
      try {
        const response = await authorizedAxios.get<Blob>(
          `${base}/context/${encodeURIComponent(contextId)}/plots/${encodeURIComponent(tag)}`,
          { responseType: 'blob' }
        )
        // Only 200 carries a PNG; 202 is the "still building" body, and an
        // object URL over that JSON would render as a broken image.
        if (response.status !== 200) return null
        return URL.createObjectURL(response.data)
      } catch {
        // A missing plot is not an error worth blocking the context view on.
        return null
      }
    },
    [authorizedAxios, base]
  )

  return {
    context,
    contextId,
    contextLoading,
    contextError,
    buildContext,
    resetContext,
    adoptContext,
    design,
    designLoading,
    designError,
    requestDesign,
    runLoading,
    runError,
    runEntryId,
    startRun,
    evaluation,
    evaluateLoading,
    evaluateError,
    evaluate,
    progress,
    fetchTagPlot,
  }
}
